import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useLocation } from "wouter";
import { apiRequest, clearCsrfToken, queryClient } from "./queryClient";

export type UserRole = "owner" | "admin" | "member" | "viewer";

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  organizationId: number | null;
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  subscriptionStatus: string;
  seatCount: number;
  trialEndsAt: string | null;
  stripeCustomerId: string | null;
  stripeConnectPayoutsEnabled: number | null;
}

export interface Impersonation {
  userId: number;
  email: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  organization: Organization | null;
  impersonating: Impersonation | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (action: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ROLE_HIERARCHY: Record<UserRole, number> = {
  owner: 4,
  admin: 3,
  member: 2,
  viewer: 1,
};

const ACTION_MIN_ROLE: Record<string, number> = {
  "manage_billing": 4,
  "invite_users": 3,
  "manage_team": 3,
  "delete_project": 3,
  "delete_inventory": 3,
  "edit_settings": 3,
  "create_project": 2,
  "edit_project": 2,
  "create_inventory": 2,
  "edit_inventory": 2,
  "view": 1,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [impersonating, setImpersonating] = useState<Impersonation | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setOrganization(data.organization);
        setImpersonating(data.impersonating ?? null);
      } else {
        clearCsrfToken();
        queryClient.clear();
        setUser(null);
        setOrganization(null);
        setImpersonating(null);
      }
    } catch {
      clearCsrfToken();
      queryClient.clear();
      setUser(null);
      setOrganization(null);
      setImpersonating(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function login(email: string, password: string) {
    const res = await apiRequest("POST", "/api/auth/login", { email, password });
    clearCsrfToken();
    queryClient.clear();
    const data = await res.json();
    setUser(data.user);
    setOrganization(data.organization);
  }

  async function logout() {
    await apiRequest("POST", "/api/auth/logout", {});
    clearCsrfToken();
    queryClient.clear();
    setUser(null);
    setOrganization(null);
    setImpersonating(null);
  }

  function can(action: string): boolean {
    if (!user) return false;
    const minLevel = ACTION_MIN_ROLE[action] ?? 1;
    return ROLE_HIERARCHY[user.role] >= minLevel;
  }

  return (
    <AuthContext.Provider value={{ user, organization, impersonating, loading, login, logout, refresh, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      setLocation("/login");
    }
  }, [user, loading, setLocation]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;
  return <>{children}</>;
}
